import {
  Component,
  OnInit,
  ViewChild,
  AfterViewInit,
  ChangeDetectionStrategy,
  inject,
  DestroyRef,
  ChangeDetectorRef
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { tap, catchError, first, switchMap, map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';

import { SalesOrderDocumentsService } from '@shared/services/api/sales-order-documents.service';
import { OdvDocumentsFormService } from '@shared/services/api/odv-documents-form.service';
import { ProfileService } from '@shared/services/api/profile.service';
import { FsSignedUrlService } from '@shared/services/api/fs-signed-url.service';
import { FsAttachmentService } from '@shared/services/api/fs-attachment.service';
import { SalesOrderDocuments } from '@shared/entities/salesOrderDocuments';
import { Profile } from '@shared/entities/profile';
import { OdvDocuments } from '@shared/entities/odvDocuments';
import { OdvHistoryComponent } from '@shared/components/odv-history/odv-history.component';
import { ThemeService } from '../services/theme.service';
import { Roles } from '@shared/models/types';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '@shared/services/toast.service';

@Component({
  selector: 'app-odv-documents-form',
  templateUrl: './odv-documents-form.component.html',
  styleUrl: './odv-documents-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OdvDocumentsFormComponent implements OnInit, AfterViewInit {
  private _destroyRef = inject(DestroyRef);
  private _cdr = inject(ChangeDetectorRef);
  private _themeService = inject(ThemeService);
  private route = inject(ActivatedRoute);
  private location = inject(Location);
  private router = inject(Router);
  private salesOrderDocumentsService = inject(SalesOrderDocumentsService);
  private odvDocumentsFormService = inject(OdvDocumentsFormService);
  private profileService = inject(ProfileService);
  private fsSignedUrlService = inject(FsSignedUrlService);
  private fsAttachmentService = inject(FsAttachmentService);
  private http = inject(HttpClient);
  private _translateService = inject(TranslateService);
  private _toastService = inject(ToastService);

  @ViewChild('odvHistory') odvHistoryComponent!: OdvHistoryComponent;

  isLight = true;
  isLoading = false;
  isUploading = false;

  public _salesOrderDocumentId: string | null = null;
  public salesOrderDocument: SalesOrderDocuments = new SalesOrderDocuments();
  public userProfile: Profile | null = null;

  selectedFile: File | null = null;

  uploadStatusMessage = '';
  uploadStatusType: 'success' | 'error' = 'success';

  odvHistoryHasFiles: boolean | null = null;
  /** Files the administrator marked for removal, applied only when this form is saved. */
  pendingRemovals = 0;

  constructor() {
    /* noop */
  }

  ngOnInit(): void {
    this.setupRouteSubscription();
    this.loadUserProfile();

    this._themeService.isLight$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap((x) => {
          this.isLight = x;
          this._cdr.markForCheck();
        })
      )
      .subscribe();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.checkOdvHistoryFiles();
      this._cdr.markForCheck();
    }, 0);
  }

  private setupRouteSubscription(): void {
    this.route.params
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap((params) => {
          this._salesOrderDocumentId = params['id'] || null;
          if (this._salesOrderDocumentId) {
            this.loadSalesOrderDocumentData();
          }
          this._cdr.markForCheck();
        })
      )
      .subscribe();
  }

  private loadUserProfile(): void {
    this.profileService.profile$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap((profile) => {
          this.userProfile = profile;
          this._cdr.markForCheck();
        })
      )
      .subscribe();
  }

  private loadSalesOrderDocumentData(): void {
    if (!this._salesOrderDocumentId) {
      this.isLoading = false;
      return;
    }

    this.isLoading = true;

    this.salesOrderDocumentsService
      .getList({ id: this._salesOrderDocumentId })
      .pipe(
        tap((paginatedResult) => {
          if (paginatedResult && paginatedResult.data && paginatedResult.data.length > 0) {
            this.salesOrderDocument = paginatedResult.data[0];
          }
          this.isLoading = false;
          this._cdr.markForCheck();
        }),
        catchError((error) => {
          console.error('Error loading sales order document:', error);
          this.isLoading = false;
          this._cdr.markForCheck();
          return of(null);
        })
      )
      .subscribe();
  }

  checkOdvHistoryFiles(): void {
    if (this.odvHistoryComponent && this.odvHistoryComponent.odvHistory$) {
      this.odvHistoryComponent.odvHistory$.subscribe((history) => {
        this.odvHistoryHasFiles = Array.isArray(history) && history.length > 0;
      });
    }
  }

  onFileSelected(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (target.files && target.files.length > 0) {
      this.selectedFile = target.files[0];
      this.resetStateFlags();
    }
  }

  removeFile(): void {
    this.selectedFile = null;
    const fileInput = document.getElementById('documentFile') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
    this.resetStateFlags();
  }

  onSubmit(): void {
    if (!this.selectedFile && this.pendingRemovals === 0) {
      this.uploadStatusMessage = 'CHOOSE_FILE_LABEL';
      this.uploadStatusType = 'error';
      return;
    }

    if (!this._salesOrderDocumentId) {
      this.uploadStatusMessage = 'DOC_ID_REQUIRED';
      this.uploadStatusType = 'error';
      return;
    }

    if (!this.userProfile) {
      this.uploadStatusMessage = 'REQUIRED_USER_PROFILE';
      this.uploadStatusType = 'error';
      return;
    }

    this.isUploading = true;
    this.clearStatusMessage();

    // Marked files are deleted here, on save, never when the button is clicked.
    const removals$ = this.pendingRemovals > 0 ? this.odvHistoryComponent.applyPendingRemovals() : of(null);

    const file = this.selectedFile;
    if (!file) {
      this.applyRemovalsOnly(removals$);
      return;
    }

    const odvDocument = this.buildOdvDocument();

    removals$
      .pipe(
        switchMap(() =>
          this.fsSignedUrlService.generateSignedUrl({
            fileName: file.name,
            contentType: file.type,
            basePath: 'attachments'
          })
        ),
        switchMap((signedUrlResponse) =>
          this.http.put(signedUrlResponse.url, file).pipe(map(() => signedUrlResponse))
        ),
        switchMap((signedUrlResponse) =>
          this.fsAttachmentService.registerAttachment({
            filePath: this.extractPathFromSignedUrl(signedUrlResponse.url),
            fileName: file.name,
            contentType: file.type
          })
        ),
        switchMap((attachment) => {
          odvDocument.documentId = attachment.id;
          return this.odvDocumentsFormService.saveOdvDocumentWithAttachmentId(odvDocument);
        }),
        first(),
        tap((response: any) => {
          this.isUploading = false;

          if (response && (response instanceof Error || response.error)) {
            this.uploadStatusMessage = response.error?.message || 'ODV_DOCUMENTS_FORM.UPLOAD_ERROR';
            this.uploadStatusType = 'error';
            return;
          }

          this.uploadStatusMessage = 'ODV_DOCUMENTS_FORM.UPLOAD_SUCCESS';
          this.uploadStatusType = 'success';
          this.selectedFile = null;

          const fileInput = document.getElementById('documentFile') as HTMLInputElement;
          if (fileInput) {
            fileInput.value = '';
          }

          if (this.odvHistoryComponent) {
            this.odvHistoryComponent.refreshHistory();
          }

          setTimeout(() => {
            this.clearStatusMessage();
          }, 5000);
        }),
        catchError((error) => {
          this.isUploading = false;
          this.uploadStatusMessage = error?.error?.message || 'ODV_DOCUMENTS_FORM.UPLOAD_ERROR';
          this.uploadStatusType = 'error';
          return of(null);
        })
      )
      .subscribe();
  }

  /**
   * The object path to register, taken from the signed URL and stripped of the bucket segment.
   *
   * Each segment has to be decoded: the URL path is percent-encoded, so a document named
   * "Contratto di prova.pdf" would otherwise be registered as "Contratto%20di%20prova.pdf",
   * which does not match the object actually stored, and registration fails with
   * "Uploaded file not found".
   */
  private extractPathFromSignedUrl(signedUrl: string): string {
    const url = new URL(signedUrl);
    const parts = url.pathname
      .split('/')
      .filter((p) => p)
      .map((p) => decodeURIComponent(p));
    return parts.length > 1 ? parts.slice(1).join('/') : decodeURIComponent(url.pathname);
  }

  private buildOdvDocument(): OdvDocuments {
    const odvDocument = new OdvDocuments();
    odvDocument.salesOrderDocumentId = this._salesOrderDocumentId!;
    odvDocument.documentId = undefined;
    odvDocument.userId = this.userProfile!.id!;
    odvDocument.userName = `${this.userProfile!.firstName} ${this.userProfile!.lastName}`.trim();
    odvDocument.uploaded = true;

    return odvDocument;
  }

  public clearStatusMessage(): void {
    this.uploadStatusMessage = '';
  }

  public resetStateFlags(): void {
    this.clearStatusMessage();
  }

  public isAdmin(): boolean {
    return this.userProfile?.role === Roles.ADMIN;
  }

  public onToggleHiddenForCustomerPartner(hidden: boolean): void {
    if (!this.salesOrderDocument.id) {
      return;
    }
    this.salesOrderDocumentsService.updateHiddenForCustomerPartner(this.salesOrderDocument.id, hidden).subscribe({
      next: (updated) => {
        this.salesOrderDocument.hiddenForCustomerPartner = updated.hiddenForCustomerPartner;
        this._cdr.markForCheck();
        this._toastService.success(this._translateService.instant('ODV_DOCUMENTS_FORM.HIDDEN_FOR_CUSTOMER_PARTNER_SAVED'));
      },
      error: () => {
        // Revert the toggle in the UI: the request failed, so the stored value is unchanged.
        this.salesOrderDocument.hiddenForCustomerPartner = !hidden;
        this._cdr.markForCheck();
        this._toastService.error(this._translateService.instant('ODV_DOCUMENTS_FORM.HIDDEN_FOR_CUSTOMER_PARTNER_ERROR'));
      }
    });
  }

  public canUploadDocument(): boolean {
    if (this.salesOrderDocument.customerCanUpload === true) {
      return true;
    }

    if (this.salesOrderDocument.customerCanUpload === false && this.userProfile) {
      return this.userProfile.role === Roles.ADMIN;
    }

    return false;
  }

  public canShowFileInput(): boolean {
    if (!this.salesOrderDocument || !this.userProfile) return false;

    if (this.salesOrderDocument.customerCanUpload === true) {
      return true;
    }

    if (this.salesOrderDocument.customerCanUpload === false) {
      return this.userProfile.role === Roles.ADMIN;
    }

    return false;
  }

  isUploadablePeriodically(): boolean {
    return !!this.salesOrderDocument.uploadablePeriodically;
  }

  getUploadButtonLabel(): string {
    if (this.isUploadablePeriodically()) {
      return 'ODV_DOCUMENTS_FORM.UPLOAD_DOCUMENT';
    }
    return 'ODV_DOCUMENTS_FORM.EDIT_ODV_DOCUMENT';
  }

  private applyRemovalsOnly(removals$: Observable<unknown>): void {
    removals$
      .pipe(
        first(),
        tap(() => {
          this.isUploading = false;
          this.uploadStatusMessage = 'ODV_DOCUMENTS_FORM.CLEAR_SUCCESS';
          this.uploadStatusType = 'success';
          this.checkOdvHistoryFiles();
          setTimeout(() => this.clearStatusMessage(), 5000);
        }),
        catchError(() => {
          this.isUploading = false;
          this.uploadStatusMessage = 'ODV_DOCUMENTS_FORM.CLEAR_ERROR';
          this.uploadStatusType = 'error';
          return of(null);
        })
      )
      .subscribe();
  }

  canSubmit(): boolean {
    return (
      (!!this.selectedFile || this.pendingRemovals > 0) &&
      !!this._salesOrderDocumentId &&
      !!this.userProfile &&
      !this.isUploading &&
      this.canShowFileInput()
    );
  }

  getYear() {
    return new Date().getFullYear();
  }

  goBack(): void {
    this.location.back();
  }

  goToList(): void {
    this.router.navigate(['/repository']);
  }
}
